import type { ClaimHistoryActivity } from '@billing/validation/claimHistory';
import { Blockquote, Button, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconExternalLink,
  IconFileText,
  IconMessageCircle,
} from '@tabler/icons-react';
import { getAttachmentDownloadURL } from 'api/resource/claimAttachment';
import { Link } from 'react-router-dom';
import { getClaimRelationshipLabelKey } from 'pages/claims/utils/claimRelationshipUtils';

export const ClaimHistoryActivityActions = ({
  activity,
}: {
  activity: ClaimHistoryActivity;
}) => {
  const { translate } = useTranslation();

  if (
    activity.activity === 'tariffCommentAdded' ||
    activity.activity === 'internalCommentAdded' ||
    activity.activity === 'internalCommentRemoved'
  ) {
    return (
      <Stack gap="xs">
        {activity.activity === 'tariffCommentAdded' && (
          <Text size="sm" fw={600}>
            {translate(
              'pages:claims.claimHistory.activities.commentTariffs',
              { tariffs: activity.tariffLabels?.join(', ') ?? '' },
              `Tariffs: ${activity.tariffLabels?.join(', ') ?? ''}`
            )}
          </Text>
        )}
        <Blockquote
          color="blue"
          radius="md"
          p="md"
          mt="xs"
          ml="xs"
          icon={<IconMessageCircle size={16} aria-hidden="true" />}
          iconSize={28}
        >
          <Text
            size="sm"
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {activity.commentText}
          </Text>
        </Blockquote>
      </Stack>
    );
  }

  if (
    (activity.activity === 'claimLinked' ||
      activity.activity === 'claimUnlinked') &&
    activity.relationshipType &&
    activity.claimSide
  ) {
    const labelKey = getClaimRelationshipLabelKey(
      activity.relationshipType,
      activity.claimSide
    );
    const relationshipLabel = translate(
      `pages:claims.relationships.labels.${labelKey}`
    );
    const relatedClaimLabel =
      activity.relatedClaimNumber || activity.relatedClaimID?.slice(0, 8) || '';

    return (
      <Stack gap="xs">
        <Text size="sm">
          {translate(
            `pages:claims.claimHistory.activities.${activity.activity}Description`,
            { relationship: relationshipLabel, claim: relatedClaimLabel },
            `${relationshipLabel}: ${relatedClaimLabel}`
          )}
        </Text>
        {activity.relatedClaimID && (
          <Button
            component="a"
            href={`/claims/edit/${activity.relatedClaimID}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
            size="xs"
            leftSection={<IconExternalLink size={14} />}
            w="fit-content"
          >
            {translate(
              'pages:claims.claimHistory.activities.openRelatedClaim',
              'Open related claim'
            )}
          </Button>
        )}
      </Stack>
    );
  }

  if (activity.activity === 'submissionCancelled') {
    return (
      <Stack gap={2}>
        <Text size="sm">
          {translate(
            'pages:claims.claimHistory.activities.submissionCancelledDescription',
            'Affected tariffs were returned to Ready because this submission was cancelled.'
          )}
        </Text>
        {activity.submissionFilename && (
          <Text size="xs" c="dimmed">
            {activity.submissionFilename}
          </Text>
        )}
      </Stack>
    );
  }

  if (activity.activity === 'claimQueried' && activity.claimQueryID) {
    return (
      <Button
        component={Link}
        to={`/queries?queryId=${encodeURIComponent(activity.claimQueryID)}`}
        variant="light"
        size="xs"
        leftSection={<IconExternalLink size={14} />}
      >
        {translate(
          'pages:claims.claimHistory.activities.viewQuery',
          'View query'
        )}
      </Button>
    );
  }

  if (activity.activity !== 'supportingDocumentsSent') return null;

  return (
    <Group gap="xs">
      {(activity.attachmentIDs ?? []).map((attachmentID, index) => (
        <Button
          key={attachmentID}
          variant="light"
          size="xs"
          leftSection={<IconFileText size={14} />}
          onClick={async () => {
            const url = await getAttachmentDownloadURL(attachmentID);
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
        >
          {activity.attachmentFilenames?.[index] ??
            translate(
              'pages:claims.claimHistory.activities.viewDocument',
              'View document'
            )}
        </Button>
      ))}
    </Group>
  );
};
